import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProductionLinesEntity } from "entities/ProductionLines/ProductionLinesEntity";
import { Formik, Form, FormikHelpers } from "formik";
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    FormGroup,
    FormControlLabel,
    Checkbox,
    FormHelperText,
    Typography,
    TextField,
    InputLabel,
    Tooltip,
    Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
    IProductionLineCorporateBOFormValues,
    IProductionLinesCreationForm,
} from "./ProductionLinesCreationFormInterfaces";
import { productionLineCorporateBOFormSchema } from "validations/schemas";
import { useStyles } from "./ProductionLinesCreationForm.styles";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { useNavigate } from "react-router-dom";
import { CORPORATE_BACKOFFICE_PATHS, DefaultTranslationFieldsInTable } from "constants/constants";
import { checkShiftOverlapping } from "utilities/utilities";
import { translateDefaultEntity } from "utilities/localesUtilities";
import moment from "moment";
import useDateFormat from "hooks/useDateFormat";

export const ProductionLinesCreationForm = ({
    millId,
    handleClose,
    productionLineEdition,
}: IProductionLinesCreationForm) => {
    const navigate = useNavigate();
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const { formatDate } = useDateFormat();

    const { apolloMutationError, addProductionLine } = ProductionLinesEntity.InsertProductionLineCorporateBO();
    const { shifts } = MillsEntity.GetMillShifts(millId);
    const [overlappingShifts, setOverlappingShifts] = useState<boolean>(false);

    const effectiveAtDate = moment.utc().add(1, "day").startOf("day").toDate();
    const hasShiftsToBeEffective = productionLineEdition?.production_line_shifts?.find(
        (shift) => new Date(shift.effective_at) > new Date(),
    );

    const handleSubmit = useCallback(
        async (
            values: IProductionLineCorporateBOFormValues,
            { setSubmitting }: FormikHelpers<IProductionLineCorporateBOFormValues>,
        ) => {
            if (overlappingShifts) {
                setSubmitting(false);
                return;
            }

            if (productionLineEdition) {
                const disableShifts = productionLineEdition?.production_line_shifts
                    .filter((shift) => !values.shiftsIds.some((id) => id === shift.shift.shift_id))
                    .map((shift) => ({
                        production_line_shift_id: shift.production_line_shift_id,
                        shift_id: shift.shift.shift_id,
                        disabled_at: effectiveAtDate,
                    }));
                const newShiftsAssigned = values.shiftsIds
                    .filter(
                        (id) =>
                            !productionLineEdition?.production_line_shifts.some((shift) => id === shift.shift.shift_id),
                    )
                    .map((shiftId) => ({
                        shift_id: shiftId,
                        effective_at: effectiveAtDate,
                    }));
                const shiftChanges = [...newShiftsAssigned, ...disableShifts];
                const productionLineValues = {
                    production_line_id: productionLineEdition.production_line_id,
                    mill_id: millId,
                    name: values.name,
                    ...(values.code && { code: values.code }),
                    ...(shiftChanges.length && {
                        production_line_shifts: {
                            data: shiftChanges,
                            on_conflict: {
                                constraint: "production_line_shift_pkey",
                                update_columns: ["disabled_at"],
                            },
                        },
                    }),
                };

                try {
                    const response = await addProductionLine({ variables: { productionLine: productionLineValues } });
                    if (response) {
                        handleClose();
                    }
                } catch {
                    return;
                }
            } else {
                const productionLineValues = {
                    name: values.name,
                    mill_id: millId,
                    ...(values.code && { code: values.code }),
                    production_line_shifts: { data: values.shiftsIds.map((shiftId) => ({ shift_id: shiftId })) },
                };

                try {
                    const response = await addProductionLine({ variables: { productionLine: productionLineValues } });
                    if (response) {
                        handleClose();
                    }
                } catch {
                    return;
                }
            }
        },
        [addProductionLine, handleClose, millId, overlappingShifts, productionLineEdition, effectiveAtDate],
    );

    const handleShiftsOverlap = useCallback(
        (ids: number[]) => {
            const isProductionLineCreation = true;
            const overlap = checkShiftOverlapping(ids, shifts, isProductionLineCreation);

            setOverlappingShifts(overlap);
        },
        [shifts],
    );

    const initFormValues: IProductionLineCorporateBOFormValues = productionLineEdition
        ? {
              name: productionLineEdition.name,
              code: productionLineEdition.code || "",
              shiftsIds: productionLineEdition.production_line_shifts.map((s) => s.shift.shift_id),
          }
        : {
              name: "",
              code: "",
              shiftsIds: [],
          };

    return (
        <Formik
            initialValues={initFormValues}
            validationSchema={productionLineCorporateBOFormSchema}
            onSubmit={handleSubmit}
        >
            {({ isSubmitting, errors, touched, values, handleChange, setFieldValue }) => {
                const noShiftChange = productionLineEdition?.production_line_shifts.every((shift) =>
                    values.shiftsIds.some((id) => shift.shift.shift_id === id),
                );

                const newShiftAdded = values.shiftsIds.every((id) =>
                    productionLineEdition?.production_line_shifts.some((shift) => shift.shift.shift_id === id),
                );
                return (
                    <Form className={classes.formContainer}>
                        <Box className={classes.boxContainer}>
                            <InputLabel id="outlined-name-label">
                                <Typography variant="subtitle2" className={classes.formLabel}>
                                    {t("corporate_backoffice.production_line_view.production_line_creation_form.name")}
                                </Typography>
                            </InputLabel>
                            <TextField
                                aria-labelledby="outlined-name-label"
                                fullWidth
                                id="outlined-name-input"
                                className={classes.textField}
                                name="name"
                                type="text"
                                variant="outlined"
                                value={values.name}
                                onChange={handleChange}
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name && t(errors.name)}
                            />
                        </Box>
                        <Box className={classes.boxContainer}>
                            <InputLabel id="outlined-code-label">
                                <Typography variant="subtitle2" className={classes.formLabel}>
                                    {t("corporate_backoffice.production_line_view.production_line_creation_form.code")}
                                </Typography>
                            </InputLabel>
                            <TextField
                                aria-labelledby="outlined-code-label"
                                fullWidth
                                id="outlined-code-input"
                                className={classes.textField}
                                name="code"
                                type="text"
                                variant="outlined"
                                value={values.code}
                                onChange={handleChange}
                            />
                        </Box>
                        <FormControl
                            error={
                                overlappingShifts ? overlappingShifts : touched.shiftsIds && Boolean(errors.shiftsIds)
                            }
                            className={classes.boxContainer}
                            component="fieldset"
                            variant="standard"
                        >
                            <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
                                <FormLabel className={classes.formLabel}>
                                    {t(
                                        "corporate_backoffice.production_line_view.production_line_creation_form.shifts",
                                    )}
                                </FormLabel>
                                {(!noShiftChange || !newShiftAdded || hasShiftsToBeEffective) &&
                                    productionLineEdition && (
                                        <Alert sx={{ alignSelf: "center", padding: "5px 10px" }} severity="info">
                                            {t(
                                                "corporate_backoffice.production_line_view.production_line_creation_form.warnings.shift_change.message",
                                                {
                                                    date: !noShiftChange
                                                        ? formatDate(effectiveAtDate)
                                                        : hasShiftsToBeEffective
                                                        ? formatDate(hasShiftsToBeEffective?.effective_at)
                                                        : formatDate(effectiveAtDate),
                                                },
                                            )}
                                        </Alert>
                                    )}
                            </div>
                            <FormGroup style={{ display: "flex", flexDirection: "row", width: "100%" }}>
                                {shifts.map((shift) => {
                                    const isShiftWithChangesToBeEffective =
                                        productionLineEdition?.production_line_shifts?.some(
                                            (plShift) =>
                                                new Date(plShift.effective_at) > new Date() &&
                                                shift.shift_id === plShift.shift.shift_id,
                                        );
                                    const newShiftChange =
                                        !productionLineEdition?.production_line_shifts.some(
                                            (plShift) => plShift.shift.shift_id === shift.shift_id,
                                        ) && values.shiftsIds.some((id) => id === shift.shift_id);
                                    const disabledShift =
                                        productionLineEdition?.production_line_shifts.some(
                                            (plShift) => plShift.shift.shift_id === shift.shift_id,
                                        ) && !values.shiftsIds.some((id) => id === shift.shift_id);
                                    return (
                                        <Tooltip
                                            key={shift.shift_id}
                                            PopperProps={{ className: classes.tooltip }}
                                            title={
                                                <>
                                                    <Typography className={classes.tooltipTexts}>
                                                        {t(
                                                            "corporate_backoffice.production_line_view.production_line_creation_form.start",
                                                        )}
                                                        : {shift.shift_historic[0]?.start_time}
                                                    </Typography>
                                                    <Typography className={classes.tooltipTexts}>
                                                        {t(
                                                            "corporate_backoffice.production_line_view.production_line_creation_form.end",
                                                        )}
                                                        : {shift.shift_historic[0]?.end_time}
                                                    </Typography>
                                                    <Typography className={classes.tooltipTexts}>
                                                        {t(
                                                            "corporate_backoffice.production_line_view.production_line_creation_form.days",
                                                        )}
                                                        :{" "}
                                                        {shift.shift_historic[0]?.shift_days &&
                                                            shift.shift_historic[0]?.shift_days
                                                                .filter((shift_day) => shift_day.is_enabled)
                                                                .map(
                                                                    (shift_day) =>
                                                                        `${translateDefaultEntity(
                                                                            i18n.language,
                                                                            shift_day.day,
                                                                            "name",
                                                                            DefaultTranslationFieldsInTable.DAY,
                                                                        )} `,
                                                                )}
                                                    </Typography>
                                                </>
                                            }
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={values.shiftsIds.some((id) => id === shift.shift_id)}
                                                        value={shift.shift_id}
                                                        onChange={(e) => {
                                                            let deepClonedShiftsIds: number[] = JSON.parse(
                                                                JSON.stringify(values.shiftsIds),
                                                            );
                                                            if (e.target.checked) {
                                                                const shiftsIds = [
                                                                    ...deepClonedShiftsIds,
                                                                    Number(e.target.value),
                                                                ];
                                                                setFieldValue("shiftsIds", [
                                                                    ...deepClonedShiftsIds,
                                                                    Number(e.target.value),
                                                                ]);
                                                                handleShiftsOverlap(shiftsIds);
                                                            } else {
                                                                const shiftsIds = deepClonedShiftsIds.filter(
                                                                    (shiftId) => Number(e.target.value) !== shiftId,
                                                                );
                                                                setFieldValue("shiftsIds", shiftsIds);
                                                                handleShiftsOverlap(shiftsIds);
                                                            }
                                                        }}
                                                        className={
                                                            isShiftWithChangesToBeEffective ||
                                                            newShiftChange ||
                                                            disabledShift
                                                                ? classes.checkboxChangesToBeEffective
                                                                : classes.checkbox
                                                        }
                                                    />
                                                }
                                                label={shift.name}
                                            />
                                        </Tooltip>
                                    );
                                })}
                            </FormGroup>
                            {overlappingShifts && (
                                <FormHelperText>
                                    {t(
                                        "corporate_backoffice.production_line_view.production_line_creation_form.errors.overlapping_shifts",
                                    )}
                                </FormHelperText>
                            )}
                            <FormHelperText>
                                {touched.shiftsIds && errors.shiftsIds && t(errors.shiftsIds)}
                            </FormHelperText>
                        </FormControl>
                        {shifts.length === 0 && (
                            <Alert severity="warning">
                                {t(
                                    "corporate_backoffice.production_line_view.production_line_creation_form.warnings.no_shifts.message",
                                )}{" "}
                                <span
                                    className={classes.setupMillLink}
                                    onClick={() =>
                                        navigate(
                                            `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.SETUP_DASHBOARD}?millId=${millId}`,
                                        )
                                    }
                                >
                                    {t(
                                        "corporate_backoffice.production_line_view.production_line_creation_form.warnings.no_shifts.link",
                                    )}
                                </span>
                            </Alert>
                        )}
                        <p style={{ display: apolloMutationError ? "block" : "none", color: "red" }}>
                            {apolloMutationError?.message.includes("uq_production_line_name")
                                ? t("setup_corporation.errors.existing_name")
                                : t("setup_corporation.errors.creation_error")}
                        </p>
                        <div className={classes.buttonsContainer}>
                            <Button className={classes.cancelButton} variant="text" onClick={handleClose}>
                                {t(
                                    "corporate_backoffice.production_line_view.production_line_creation_form.cancelButton",
                                )}
                            </Button>
                            <Button
                                className={classes.addButton}
                                variant="outlined"
                                type="submit"
                                disabled={isSubmitting || overlappingShifts}
                            >
                                {productionLineEdition ? (
                                    t(
                                        "corporate_backoffice.production_line_view.production_line_creation_form.edit_button",
                                    )
                                ) : (
                                    <>
                                        <AddIcon />{" "}
                                        {t(
                                            "corporate_backoffice.production_line_view.production_line_creation_form.addButton",
                                        )}
                                    </>
                                )}
                            </Button>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};
